import type {
  AeExportRange,
  AeExportSettings,
  AeExportSettingsInput,
} from '@frameio/graphql-client/generated';
import {
  useExportSettingsManager,
  type UseExportSettingsManagerReturnType,
  useExportSettingsValidation,
} from '../../shared/hooks';
import {
  useGetAeExportSettingsQuery,
  useUpdateAeExportSettingsMutation,
} from '../gql';

export type UseCompositionExportSettingsReturnType =
  UseExportSettingsManagerReturnType<AeExportSettings> & {
    range: AeExportRange;
    settingsError: string | undefined;
  };

const DEFAULT_SETTINGS: AeExportSettings = {
  assetFolderId: null,
  assetFolderName: null,
  exportMarkersAsCommentsEnabled: false,
  localCopyEnabled: false,
  localFolderPath: null,
  outputModuleTemplate: null,
  renderSettingTemplate: null,
  range: 'entireComposition',
};

const GQL_UXP_CONTEXT = {
  context: {
    resolveWithUXP: true,
  },
};

export const useCompositionExportSettings = ({
  overrideFolderId,
}: {
  overrideFolderId?: string;
}): UseCompositionExportSettingsReturnType => {
  const {
    data: settingsData,
    loading,
    refetch,
  } = useGetAeExportSettingsQuery(GQL_UXP_CONTEXT);

  const [storeSettingsMutation] =
    useUpdateAeExportSettingsMutation(GQL_UXP_CONTEXT);

  const storeMutation = async (
    input: AeExportSettingsInput
  ): Promise<AeExportSettings | null> => {
    const result = await storeSettingsMutation({
      variables: { input },
    });
    const updated = result.data?.updateAeExportSettings ?? null;
    if (updated) {
      refetch();
    }
    return updated;
  };

  const base = useExportSettingsManager<AeExportSettings>({
    defaultSettings: DEFAULT_SETTINGS,
    uxpStorage: settingsData?.getAeExportSettings ?? null,
    loading,
    storeMutation,
    overrideFolderId,
  });

  const settingsError = useExportSettingsValidation({
    assetFolderId: base.assetFolderId,
    localCopyEnabled: base.localCopyEnabled,
    localFolderPath: base.localFolderPath,
  });

  return {
    ...base,
    range: base.range ?? 'entireComposition',
    settingsError,
  };
};
